/**
 * ImageViewer - Zoomable/pannable image viewer for logbook photos
 * Industrial precision controls with scientific instrument aesthetics
 */

import { useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/cn';
import { ZoomIn, ZoomOut, RotateCw, Maximize2, Move } from 'lucide-react';

interface ImageViewerProps {
  src: string;
  alt?: string;
  className?: string;
}

export function ImageViewer({ src, alt = 'Logbook image', className }: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 4;
  const SCALE_STEP = 0.25;

  const handleZoomIn = useCallback(() => {
    setScale(s => Math.min(MAX_SCALE, s + SCALE_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(s => Math.max(MIN_SCALE, s - SCALE_STEP));
  }, []);

  const handleRotate = useCallback(() => {
    setRotation(r => (r + 90) % 360);
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Mouse drag handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [scale, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
    setScale(s => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + delta)));
  }, []);

  const displayPosition = scale > 1 ? position : { x: 0, y: 0 };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Toolbar - Industrial control panel style */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-1">
          <ToolButton onClick={handleZoomOut} title="Zoom Out" disabled={scale <= MIN_SCALE}>
            <ZoomOut className="w-4 h-4" />
          </ToolButton>
          <div className="px-2 py-1 min-w-[4rem] text-center font-mono text-xs text-emerald-400 bg-gray-800 rounded border border-gray-700">
            {Math.round(scale * 100)}%
          </div>
          <ToolButton onClick={handleZoomIn} title="Zoom In" disabled={scale >= MAX_SCALE}>
            <ZoomIn className="w-4 h-4" />
          </ToolButton>
        </div>

        <div className="flex items-center gap-1">
          <ToolButton onClick={handleRotate} title="Rotate 90°">
            <RotateCw className="w-4 h-4" />
          </ToolButton>
          <ToolButton onClick={handleReset} title="Reset View">
            <Maximize2 className="w-4 h-4" />
          </ToolButton>
        </div>
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-hidden bg-gray-950 relative',
          scale > 1 ? 'cursor-grab' : 'cursor-default',
          isDragging && 'cursor-grabbing'
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        {/* Grid background pattern - laboratory aesthetic */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(to right, #fff 1px, transparent 1px),
              linear-gradient(to bottom, #fff 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
          }}
        />

        {/* Loading state */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-400 font-mono">Loading image...</span>
            </div>
          </div>
        )}

        {/* Error state */}
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="text-center">
              <div className="text-red-500 mb-2">Failed to load image</div>
              <div className="text-xs text-gray-500 font-mono">{src}</div>
            </div>
          </div>
        )}

        {/* Image */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `translate(${displayPosition.x}px, ${displayPosition.y}px)`,
          }}
        >
          <img
            src={src}
            alt={alt}
            className={cn(
              'max-w-full max-h-full object-contain transition-transform duration-150',
              isDragging && 'transition-none'
            )}
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
            }}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
            draggable={false}
          />
        </div>

        {/* Zoom/pan indicator */}
        {scale > 1 && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-gray-900/80 rounded text-xs text-gray-400">
            <Move className="w-3 h-3" />
            Drag to pan
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ToolButton - Industrial control button
 */
interface ToolButtonProps {
  onClick: () => void;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}

function ToolButton({ onClick, title, disabled, children }: ToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={cn(
        'p-1.5 rounded',
        'text-gray-400 hover:text-white',
        'hover:bg-gray-700',
        'transition-colors',
        'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400'
      )}
    >
      {children}
    </button>
  );
}
